import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';

import type { CfiRating } from '~/data/hooks/useCfi';
import { BUTTON_SX } from '~/theme';

import { ratingColors, ratingLabels } from './ratingLabels';

const RATING_OPTIONS: CfiRating[] = ['favorable', 'neutral', 'unfavorable', 'not_applicable'];

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: {
    provisionLabel: string;
    rating: CfiRating;
    rationale: string;
  }) => Promise<void>;
};

export const CfiAddCustomFindingDialog = ({ open, onClose, onSubmit }: Props) => {
  const [label, setLabel] = useState('');
  const [rating, setRating] = useState<CfiRating>('neutral');
  const [rationale, setRationale] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setLabel('');
    setRating('neutral');
    setRationale('');
    setError(null);
  };

  const handleClose = () => {
    if (isBusy) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!label.trim() || !rationale.trim()) {
      setError('Title and rationale are required.');
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      await onSubmit({
        provisionLabel: label.trim(),
        rating,
        rationale: rationale.trim(),
      });
      reset();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to add finding.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add custom finding</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Vendor lock-in clause"
            fullWidth
            size="small"
          />
          <TextField
            select
            label="Rating"
            value={rating}
            onChange={(e) => setRating(e.target.value as CfiRating)}
            sx={{ width: { xs: '100%', sm: 240 } }}
            size="small"
          >
            {RATING_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                <Chip
                  size="small"
                  color={ratingColors[option]}
                  label={ratingLabels[option]}
                  sx={{ pointerEvents: 'none' }}
                />
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            multiline
            minRows={3}
            maxRows={10}
            fullWidth
            size="small"
          />
          {/* An Alert, as everywhere else in the app — this was a disabled
              TextField, which rendered the message greyed out inside a box that
              looked like one more field to fill in. */}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isBusy} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isBusy} sx={BUTTON_SX}>
          Add finding
        </Button>
      </DialogActions>
    </Dialog>
  );
};
